import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import type { PublicUser } from '@/types/user.types';
import { userApi } from '@/services/api.service';
import { authStorage } from '@/services/authStorage.service';
import { identifyUser } from '@/utils/logger.util';

export const CURRENT_USER_QUERY_KEY = ['currentUser'] as const;

interface UseCurrentUserResult {
  user: PublicUser | undefined;
  isAuthenticated: boolean;
  isLoading: boolean;
  isError: boolean;
}

export const useCurrentUser = (): UseCurrentUserResult => {
  const hasToken = Boolean(authStorage.getAccessToken());

  const query = useQuery<PublicUser>({
    queryKey: CURRENT_USER_QUERY_KEY,
    queryFn: () => userApi.getMe(),
    enabled: hasToken,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // Every error report from here on names the admin it happened to; cleared on sign-out so a
  // shared machine never attributes one admin's errors to another.
  useEffect(() => {
    identifyUser(query.data ? { id: query.data.id, role: query.data.role } : null);
  }, [query.data]);

  return {
    user: query.data,
    isAuthenticated: hasToken && Boolean(query.data),
    isLoading: query.isLoading,
    isError: query.isError,
  };
};
